import {
	Button,
	type ButtonVariant,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	Flex,
	FlexItem,
	Grid,
	GridItem,
	Title,
} from "@patternfly/react-core";
import { GripVerticalIcon } from "@patternfly/react-icons";
import type { FC, ReactNode } from "react";
import type { DragHandleProps } from "../../../constants/CardsStructure";
import { DashboardCard, type DashboardCardProps } from "./DashboardCard";

export interface DashboardCardPropsWithVisibility extends DashboardCardProps {
	visibility: boolean;
}

interface NestedDashBoardCardProps {
	title: string;
	logo?: string;
	logoPosition?: "left" | "right";
	logoAlt?: string;
	cards?: DashboardCardPropsWithVisibility[];
	cardSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
	bodyNode?: ReactNode;
	footerItems?: { link: string; label: string; openNewTab?: boolean; variant?: ButtonVariant }[];
	footerNode?: ReactNode;
	dragHandleProps?: DragHandleProps | undefined;
}

export const NestedDashBoardCard: FC<NestedDashBoardCardProps> = ({
	title,
	logo,
	cards = [],
	cardSpan = 3,
	logoAlt = "logo",
	footerItems = [],
	logoPosition = "left",
	bodyNode,
	footerNode,
	dragHandleProps,
}) => {
	return (
		<GridItem span={12} className="authenticated-dashboard__nested-card">
			<Card data-analytics-category={title} isFullHeight className="dashboard-widget">
				<CardHeader
					{...(dragHandleProps && {
						actions: {
							actions: (
								<Button
									icon={<GripVerticalIcon />}
									variant="plain"
									aria-label="Drag to reorder"
									{...dragHandleProps.attributes}
									{...dragHandleProps.listeners}
								/>
							),
							hasNoOffset: true,
						},
					})}
				>
					<Flex direction={{ default: "row" }} alignItems={{ default: "alignItemsCenter" }}>
						{logo && logoPosition === "left" && (
							<FlexItem>
								<img src={logo} aria-hidden={true} alt={logoAlt} className="authenticated-dashboard__img" />
							</FlexItem>
						)}
						<FlexItem spacer={{ default: "spacerSm" }}>
							<Title headingLevel="h2">{title}</Title>
						</FlexItem>
						{logo && logoPosition === "right" && (
							<FlexItem>
								<img src={logo} aria-hidden={true} alt={logoAlt} className="authenticated-dashboard__img" />
							</FlexItem>
						)}
					</Flex>
				</CardHeader>
				{(bodyNode || cards) && (
					<CardBody>
						{cards?.length > 0 && (
							<Grid hasGutter>
								{cards?.map((elm, index) => {
									if (elm.visibility) {
										return (
											<GridItem span={cardSpan} key={index}>
												<DashboardCard {...elm} />
											</GridItem>
										);
									}
									return null;
								})}
							</Grid>
						)}
						{bodyNode && bodyNode}
					</CardBody>
				)}
				{(footerItems || footerNode) && (
					<CardFooter>
						{footerItems?.length > 0 && (
							<Flex>
								{footerItems?.map((elm, index) => (
									<FlexItem key={index}>
										<Button
											component="a"
											href={elm?.link}
											{...(elm?.openNewTab ? { target: "_blank" } : {})}
											{...(elm?.variant ? { variant: elm.variant } : { variant: "link" })}
											isInline
										>
											{elm?.label}
										</Button>
									</FlexItem>
								))}
							</Flex>
						)}
						{footerNode && footerNode}
					</CardFooter>
				)}
			</Card>
		</GridItem>
	);
};
